import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Check, MessageCircle, Phone, ShieldCheck, CalendarClock, Star, ArrowUpRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import CarGallery from "../components/CarGallery";
import Finance from "../components/Finance";
import LeadForm from "../components/LeadForm";
import MobileContactBar from "../components/MobileContactBar";
import useReveal from "../hooks/useReveal";
import { fetchCar } from "../api";
import { viewContent as metaViewContent } from "../lib/meta";
import { CONTACTS, TEL_HREF, whatsappHref, DELIVERIES, GOOGLE_REVIEWS_URL } from "../mock";

const GROUPS = [
  {
    title: "Dati principali",
    keys: [
      "Numero di proprietari", "Anno di immatricolazione", "Allestimento",
      "Potenza veicolo", "Chilometraggio", "Tipologia carburante",
      "Impianto gpl / metano", "Tipologia di cambio", "Tipo trazione",
      "Costo Passaggio di proprietà", "Garanzia produttore",
    ],
  },
  {
    title: "Manutenzione e scadenze",
    keys: [
      "Scadenza bollo", "Scadenza revisione", "Tipologia tagliandi",
      "Data ultimo tagliando", "Km ultimo tagliando", "Libretto manutenzione",
    ],
  },
  {
    title: "Stato d'uso e usura",
    keys: [
      "Frizione", "Motore", "Tipologia gomme", "Stato degli pneumatici",
      "Misura Pneumatici", "Quantità cerchi e gomme", "Stato interni",
      "Stato degli ammortizzatori", "Stato della carrozzeria", "Dischi e pastiglie",
      "Usura impianto frenante ant.", "Usura impianto frenante post.", "Riduttore 4x4",
      "Climatizzatore", "Turbocompressore", "Impianto di scarico", "Filtro anti partic./ D P F",
      "Impianto di raffreddamento", "Pompa carburante", "Differenziale", "Giunti e semiassi",
      "Pompa iniezione", "Volano", "Scatola guida", "Idroguida/ Servosterzo elettr.",
      "Cuscinetti ruota", "Difetti elettrici", "Difetti meccanici", "Difetti di carrozzeria",
      "Altri danni / segnalazioni",
    ],
  },
  {
    title: "Documenti e dotazione",
    keys: [
      "Doppie chiavi", "Certificato proprietà", "Kit gonfiaggio",
      "Ruota di scorta", "Libretto di circolazione",
    ],
  },
];

function SpecTable({ title, specs, keys }) {
  const rows = keys.filter((k) => specs[k] && String(specs[k]).trim());
  if (!rows.length) return null;
  return (
    <div>
      <p className="label-mono mb-2 border-b border-[var(--cm-line)] pb-3">{title}</p>
      {rows.map((k) => (
        <div key={k} className="flex items-start justify-between gap-6 border-b border-[var(--cm-line)] py-3.5">
          <span className="text-[0.92rem] text-[#7a7a72]">{k}</span>
          <span className="text-right text-[0.92rem] font-medium">{specs[k]}</span>
        </div>
      ))}
    </div>
  );
}

const immat = (v) => {
  const m = /(\d{4})-(\d{2})/.exec(v || "");
  return m ? `${m[2]}/${m[1]}` : v || "";
};

function buildVerify(s) {
  const cols = [];
  if (s["Numero di proprietari"]) {
    const np = String(s["Numero di proprietari"]).trim();
    cols.push({
      n: "01", title: "Origine e provenienza", badge: "Documentato",
      text: `${isNaN(np) ? np : `${np} proprietari`} secondo la documentazione consegnata.`,
    });
  }
  if (s["Altri danni / segnalazioni"]) {
    cols.push({
      n: "02", title: "Danni e segnalazioni", badge: "Documentato",
      text: `Altri danni / segnalazioni: ${s["Altri danni / segnalazioni"]}.`,
    });
  }
  if (s["Stato della carrozzeria"] || s["Stato interni"]) {
    const parts = [];
    if (s["Stato della carrozzeria"]) parts.push(`Carrozzeria: ${s["Stato della carrozzeria"]}`);
    if (s["Stato interni"]) parts.push(`Interni: ${s["Stato interni"]}`);
    cols.push({ n: "03", title: "Stato d'uso", verified: true, text: `${parts.join(" · ")}.` });
  }
  if (s["Data ultimo tagliando"]) {
    cols.push({
      n: "04", title: "Manutenzione", badge: "Documentato",
      text: `Ultimo tagliando ${s["Data ultimo tagliando"]}${s["Km ultimo tagliando"] ? ` a ${s["Km ultimo tagliando"]} km` : ""}. Tagliandi: ${s["Tipologia tagliandi"] || "n/d"}.`,
    });
  }
  return cols;
}

export default function CarDetail() {
  const { handle } = useParams();
  const [car, setCar] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [leadOpen, setLeadOpen] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  useReveal(car ? car.handle : null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setCar(null);
    setNotFound(false);
    fetchCar(handle).then(setCar).catch(() => setNotFound(true));
  }, [handle]);

  useEffect(() => {
    if (!car) return;
    const value = parseInt(String(car.price || "").replace(/[^0-9]/g, ""), 10) || 0;
    metaViewContent({
      contentIds: car.shopify_id != null ? [String(car.shopify_id)] : undefined,
      contentName: `${car.brand} ${car.trim}`.trim(),
      value,
      currency: "EUR",
    });
  }, [car]);

  if (notFound)
    return (
      <>
        <Header />
        <div className="mx-auto max-w-[1360px] px-6 pt-40 lg:px-10">
          <p className="text-[#6f6f68]">Auto non trovata o non più disponibile.</p>
          <Link to="/auto" className="mt-4 inline-block underline underline-offset-4">
            Torna alle auto disponibili
          </Link>
        </div>
        <Footer />
      </>
    );

  if (!car)
    return (
      <>
        <Header />
        <div className="mx-auto max-w-[1360px] animate-pulse px-6 pt-40 lg:px-10">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
            <div className="aspect-[4/3] rounded-[18px] bg-black/5" />
            <div className="space-y-4">
              <div className="h-8 w-2/3 rounded bg-black/5" />
              <div className="h-6 w-1/3 rounded bg-black/5" />
              <div className="h-12 w-full rounded bg-black/5" />
            </div>
          </div>
        </div>
        <Footer />
      </>
    );

  const s = car.specs || {};
  const verify = buildVerify(s);
  const optional = s["Optional aggiuntivi / qualificanti"];
  const carLabel = `${car.brand} ${car.trim}`.trim();
  const priceNum = parseInt((car.price || "0").replace(/[^0-9]/g, ""), 10) || 0;
  const _r = 7.9 / 1200;
  const estRata = priceNum ? Math.round((priceNum * _r) / (1 - Math.pow(1 + _r, -84))) : 0;
  const waText = `Ciao Carmaps, sono interessato a ${carLabel} (Rif. ${car.ref}). È disponibile? ${window.location.href}`;

  return (
    <>
      <Header />
      <main className="pb-24 pt-[100px] lg:pb-0 lg:pt-[120px]">
        <div className="mx-auto max-w-[1360px] px-6 lg:px-10">
          <Link
            to="/auto"
            className="inline-flex items-center gap-2 text-[0.85rem] text-[#6f6f68] transition-colors hover:text-[var(--cm-ink)]"
          >
            <ArrowLeft size={15} /> Auto disponibili
          </Link>

          <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-[1.3fr_0.9fr] lg:gap-14">
            <CarGallery images={car.images} title={car.title} video={car.video} />

            <div className="lg:sticky lg:top-24 lg:self-start">
              <h1 className="text-[clamp(1.7rem,3vw,2.3rem)] font-semibold leading-tight">
                {car.brand}{" "}
                <span className="font-normal text-[#6f6f68]">{car.trim}</span>
              </h1>
              {(() => {
                const parts = [];
                const im = immat(s["Anno di immatricolazione"]);
                if (im) parts.push(`Immatricolazione ${im}`);
                if (car.km) parts.push(car.km);
                const np = s["Numero di proprietari"] ? String(s["Numero di proprietari"]).trim() : "";
                if (np) parts.push(isNaN(np) ? np : `${np} proprietari`);
                return parts.length ? (
                  <p className="mt-2 text-[0.95rem] text-[#7a7a72]">{parts.join(" · ")}</p>
                ) : null;
              })()}
              <p className="mt-1 font-mono-cm text-[0.66rem] uppercase tracking-[0.16em] text-[#9a9a92]">
                Rif. {car.ref}
              </p>

              <div className="mt-4 flex items-end gap-3">
                <span className="text-[2rem] font-semibold">{car.price}</span>
                {car.oldPrice && (
                  <span className="mb-1.5 text-[1rem] text-[#a0a098] line-through">
                    {car.oldPrice}
                  </span>
                )}
              </div>

              {estRata > 0 && (
                <p className="mt-3 text-[0.88rem] leading-relaxed text-[#5b5b54]">
                  Importo interamente finanziabile. Tua a partire da{" "}
                  <span className="font-semibold text-[var(--cm-ink)]">
                    {estRata.toLocaleString("it-IT")} €/mese
                  </span>{" "}
                  <button
                    type="button"
                    data-testid="finance-estimate-link"
                    onClick={() => document.getElementById("finanziamento")?.scrollIntoView({ behavior: "smooth" })}
                    className="underline underline-offset-2 hover:text-[var(--cm-ink)]"
                  >
                    *fai una stima
                  </button>
                </p>
              )}

              <div className="mt-6 flex flex-col gap-3">
                <button
                  onClick={() => setBookOpen(true)}
                  data-testid="book-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[var(--cm-ink)] px-6 py-4 text-[0.92rem] font-medium text-white transition-transform hover:scale-[1.02] [&_svg]:text-white"
                >
                  <CalendarClock size={16} /> Richiedi il blocco del veicolo
                </button>
                <p className="-mt-1 text-[0.82rem] leading-relaxed text-[#6f6f68]" data-testid="book-cta-note">
                  Invii una richiesta di blocco: verifichiamo disponibilità e condizioni e ti ricontattiamo. Visione e prova su appuntamento.
                </p>
                <button
                  onClick={() => setLeadOpen(true)}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-[var(--cm-line)] px-6 py-3.5 text-[0.9rem] font-medium transition-colors hover:border-[var(--cm-ink)]"
                >
                  Richiedi informazioni
                </button>
                <div className="grid grid-cols-2 gap-3">
                  <a
                    href={whatsappHref(waText)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-[var(--cm-line)] px-4 py-3.5 text-[0.9rem] font-medium transition-colors hover:border-[var(--cm-ink)]"
                  >
                    <MessageCircle size={15} /> WhatsApp
                  </a>
                  <a
                    href={TEL_HREF}
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-[var(--cm-line)] px-4 py-3.5 text-[0.9rem] font-medium transition-colors hover:border-[var(--cm-ink)]"
                  >
                    <Phone size={15} /> Chiama
                  </a>
                </div>
              </div>

              {/* Come funziona - only confirmed conditions */}
              <div className="mt-6 rounded-[14px] border border-[var(--cm-line)] bg-white p-5">
                <p className="label-mono mb-3">Dalla richiesta alla visita</p>
                <ol className="space-y-2 text-[0.88rem] text-[#4a4a45]">
                  <li>1. Invii la richiesta con i tuoi recapiti.</li>
                  <li>2. Ti ricontattiamo per confermare la disponibilità e organizzare la visione e la prova su appuntamento, presso la sede o nel luogo concordato.</li>
                  <li>3. Decidi con calma se procedere all'acquisto.</li>
                </ol>
                <p className="mt-3 text-[0.78rem] text-[#9a9a92]">
                  Ufficio {CONTACTS.ufficio} · {CONTACTS.hours}
                </p>
              </div>
            </div>
          </div>

          {/* Verifica - only documented data */}
          {verify.length > 0 && (
            <section className="cm-reveal mt-16 border-t border-[var(--cm-line)] pt-14">
              <p className="label-mono mb-5">La verifica Carmaps</p>
              <h2 className="h-display text-[clamp(1.9rem,3.6vw,2.9rem)]">
                Cosa sappiamo di questa auto
              </h2>
              <div className="mt-10 grid grid-cols-1 gap-8 border-t border-[var(--cm-line)] pt-8 sm:grid-cols-2 lg:grid-cols-4">
                {verify.map((v) => (
                  <div key={v.n}>
                    <p className="font-mono-cm text-[0.68rem] uppercase tracking-[0.12em]">
                      <span className="text-[var(--cm-green)]">{v.n}</span>{" "}
                      <span className="text-[#8a8a82]">{v.title}</span>
                    </p>
                    <span
                      className={`mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-mono-cm text-[0.6rem] uppercase tracking-[0.1em] ${
                        v.verified
                          ? "bg-[var(--cm-green)] text-white"
                          : "border border-[var(--cm-line)] text-[#6f6f68]"
                      }`}
                    >
                      {v.verified ? <ShieldCheck size={12} /> : <Check size={12} />}
                      {v.verified ? "Verificato" : v.badge}
                    </span>
                    <p className="mt-4 text-[0.9rem] leading-relaxed text-[#6f6f68]">{v.text}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Scheda - only if there are specs */}
          {Object.keys(s).length > 0 && (
            <section className="cm-reveal pt-16">
              <h2 className="h-display text-[clamp(1.7rem,3vw,2.4rem)]">Scheda Carmaps</h2>
              <p className="mt-3 text-[0.98rem] text-[#6f6f68]">
                Ogni voce è stata compilata da Carmaps durante il controllo della vettura.
              </p>
              <div className="mt-10 grid grid-cols-1 gap-x-16 gap-y-12 lg:grid-cols-2">
                {GROUPS.map((g) => (
                  <SpecTable key={g.title} title={g.title} specs={s} keys={g.keys} />
                ))}
              </div>

              {optional && (
                <div className="mt-14">
                  <p className="label-mono mb-3 border-b border-[var(--cm-line)] pb-3">
                    Dotazione e optional
                  </p>
                  <p className="max-w-[900px] text-[0.95rem] leading-relaxed text-[#4a4a45]">
                    {optional}
                  </p>
                </div>
              )}
            </section>
          )}

          {/* Finanziamento */}
          <div className="mt-16">
            <Finance price={parseInt((car.price || "0").replace(/[^0-9]/g, ""), 10) || 0} />
          </div>

          {/* Consegne reali + recensioni Google (dati reali) */}
          <section className="cm-reveal mt-16 border-t border-[var(--cm-line)] pt-14 lg:mb-24" data-testid="detail-deliveries">
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
              <div>
                <p className="label-mono mb-3">Auto vere. Persone vere.</p>
                <h2 className="h-display text-[clamp(1.5rem,3vw,2.2rem)]">Consegne reali Carmaps</h2>
              </div>
              <a
                href={GOOGLE_REVIEWS_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="detail-google-reviews"
                className="inline-flex items-center gap-2 self-start rounded-full border border-[var(--cm-line)] px-5 py-3 text-[0.88rem] font-medium transition-all duration-200 hover:border-[var(--cm-ink)] sm:self-end"
              >
                <Star size={15} className="fill-[var(--cm-green)] text-[var(--cm-green)]" />
                Recensioni reali su Google
                <ArrowUpRight size={15} />
              </a>
            </div>
            <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
              {DELIVERIES.map((src, i) => (
                <div
                  key={src}
                  className="group aspect-[4/3] overflow-hidden rounded-[16px] border border-[var(--cm-line)]"
                >
                  <img
                    src={src}
                    alt={`Consegna Carmaps ${i + 1}`}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              ))}
            </div>
          </section>
        </div>
      </main>
      <Footer />
      <LeadForm open={leadOpen} onClose={() => setLeadOpen(false)} mode="info" car={car} />
      <LeadForm open={bookOpen} onClose={() => setBookOpen(false)} mode="booking" car={car} />
      <MobileContactBar waText={waText} />
    </>
  );
}
